import type { FullPageModel } from '@sosocio/creator-types/app';
import type {
	OfferingFoldAxis,
	OfferingModel,
} from '@sosocio/creator-types/database';
import type { PageModel } from '@sosocio/creator-types/project';

type GreetingCardOffering = Pick<OfferingModel, 'jsonSchema'> & Partial<Pick<OfferingModel, 'panel' | 'panelfold'>>;

export type GreetingCardFoldAxis = 'horizontal' | 'vertical';

export type GreetingCardPageLabelKey = 'views.greetingCards.front' | 'views.greetingCards.inside' | 'views.greetingCards.back';

export interface GreetingCardSide {
	id: 'front' | 'inside' | 'inside-first' | 'inside-second' | 'back';
	labelKey: GreetingCardPageLabelKey | 'views.greetingCards.insideLeft' | 'views.greetingCards.insideRight' | 'views.greetingCards.insideTop' | 'views.greetingCards.insideBottom';
	pageId: PageModel['id'];
	pageIndex: number;
	viewport: {
		height: number;
		width: number;
		x: number;
		y: number;
	};
}

/**
 * Resolves the direction in which the inside expands, independent of the closed card's orientation.
 * Half-fold metadata names the crease axis, perpendicular to the inside expansion.
 * Existing page geometry remains a fallback when no supported fold metadata is available.
 *
 * @param offering Offering containing the physical fold configuration in its JSON specification or legacy panel columns.
 * @param pages Ordered front, inside and back page geometries.
 * @returns Horizontal for a doubled-width inside, vertical for a doubled-height inside.
 */
export function getGreetingCardFoldAxis(
	offering: GreetingCardOffering,
	pages: readonly Pick<PageModel, 'width' | 'height'>[],
): GreetingCardFoldAxis {
	const creaseAxis = getHalfFoldCreaseAxis(offering);

	if (creaseAxis) {
		return creaseAxis === 'horizontal' ? 'vertical' : 'horizontal';
	}

	const [
		front,
		inside,
	] = pages;

	if (
		front?.width > 0
		&& front.height > 0
		&& inside?.width > 0
		&& inside.height > 0
	) {
		const horizontalExpansion = inside.width / front.width;
		const verticalExpansion = inside.height / front.height;

		if (horizontalExpansion !== verticalExpansion) {
			return horizontalExpansion > verticalExpansion ? 'horizontal' : 'vertical';
		}
	}

	return 'horizontal';
}

/**
 * Returns the shared label for one of the three persisted card pages.
 *
 * @param pageIndex Zero-based front, inside or back page index.
 * @returns Translation key, or undefined for an index outside the three-page contract.
 */
export function getGreetingCardPageLabelKey(pageIndex: number): GreetingCardPageLabelKey | undefined {
	const keys: GreetingCardPageLabelKey[] = [
		'views.greetingCards.front',
		'views.greetingCards.inside',
		'views.greetingCards.back',
	];

	return keys[pageIndex];
}

/**
 * Builds card navigation views without splitting, resizing or copying persisted page models.
 * Mobile inside views share one page id and expose their bounds in that page's coordinate system.
 * Sides follow each page's physical index, so pages filtered out for editing never shift the labels.
 *
 * @param offering Offering identifying a supported half-fold product through its JSON specification or legacy panel columns.
 * @param pages Persisted front, inside and back pages with their physical project indices.
 * @param isMobile Whether the inside should be navigated as two cropped views.
 * @returns Three desktop views or four mobile views, omitting any pages not supplied.
 */
export function getGreetingCardSides(
	offering: GreetingCardOffering | null | undefined,
	pages: readonly Pick<FullPageModel, 'index' | 'model'>[],
	isMobile = false,
): GreetingCardSide[] {
	if (!isHalfFoldOffering(offering)) {
		return [];
	}

	const foldAxis = getGreetingCardFoldAxis(
		offering ?? {},
		pages.map((page) => page.model),
	);
	const sides: GreetingCardSide[] = [];
	const sideIds: GreetingCardSide['id'][] = [
		'front',
		'inside',
		'back',
	];

	[...pages].sort((left, right) => left.index - right.index).forEach(({
		index: pageIndex,
		model: page,
	}) => {
		const labelKey = getGreetingCardPageLabelKey(pageIndex);

		if (!labelKey) {
			return;
		}

		if (isMobile && pageIndex === 1) {
			const horizontal = foldAxis === 'horizontal';
			const width = horizontal ? page.width / 2 : page.width;
			const height = horizontal ? page.height : page.height / 2;

			sides.push(
				{
					id: 'inside-first',
					labelKey: horizontal ? 'views.greetingCards.insideLeft' : 'views.greetingCards.insideTop',
					pageId: page.id,
					pageIndex,
					viewport: {
						height,
						width,
						x: 0,
						y: 0,
					},
				},
				{
					id: 'inside-second',
					labelKey: horizontal ? 'views.greetingCards.insideRight' : 'views.greetingCards.insideBottom',
					pageId: page.id,
					pageIndex,
					viewport: {
						height,
						width,
						x: horizontal ? width : 0,
						y: horizontal ? 0 : height,
					},
				},
			);
		} else {
			sides.push({
				id: sideIds[pageIndex],
				labelKey,
				pageId: page.id,
				pageIndex,
				viewport: {
					height: page.height,
					width: page.width,
					x: 0,
					y: 0,
				},
			});
		}
	});

	return sides;
}

/**
 * Reads the crease axis of a supported half-fold offering. A fold declared in the JSON specification
 * decides on its own; offerings without one fall back to the legacy `panel` flag and `panelfold` axis.
 * Both fields of either source must be valid, so incomplete metadata never activates the editor.
 *
 * @param offering Offering or lightweight specification context to read.
 * @returns The crease axis, or undefined when the offering is not a supported half-fold.
 */
function getHalfFoldCreaseAxis(offering?: GreetingCardOffering | null): OfferingFoldAxis | undefined {
	const fold: unknown = offering?.jsonSchema?.fold;

	if (fold !== undefined && fold !== null) {
		return typeof fold === 'object'
			&& !Array.isArray(fold)
			&& 'type' in fold
			&& fold.type === 'half-fold'
			&& 'axis' in fold
			&& isFoldAxis(fold.axis)
			? fold.axis
			: undefined;
	}

	return offering?.panel && isFoldAxis(offering.panelfold) ? offering.panelfold : undefined;
}

/**
 * Narrows a stored fold axis to the two supported crease directions.
 *
 * @param axis Axis from the JSON specification or the legacy `panelfold` column.
 * @returns Whether the axis is horizontal or vertical.
 */
function isFoldAxis(axis: unknown): axis is OfferingFoldAxis {
	return axis === 'horizontal' || axis === 'vertical';
}

/**
 * Identifies supported half-fold products independently of their catalogue group, from the JSON
 * specification or, when it declares no fold, the legacy `panel` and `panelfold` columns.
 * Both required fold fields must be valid before the three-surface editor can activate.
 *
 * @param offering Offering or lightweight specification context to classify.
 * @returns Whether the offering declares a valid half-fold configuration.
 */
export function isHalfFoldOffering(offering?: GreetingCardOffering | null): boolean {
	return getHalfFoldCreaseAxis(offering) !== undefined;
}
